import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Compass, X } from 'lucide-react';

/**
 * Sovereign Anchor — persistent floating button.
 * Tap → Global state interrupt → 120-second Reset (Stage 1) → Seat (Stage 9) sequence.
 * Hidden on auth/onboarding routes.
 */
const HIDDEN_ROUTES = ['/login', '/onboarding', '/forgot-password', '/reset-password'];
const TOTAL_DURATION = 120; // seconds

// Phase plan over 120s
//  0–10s:  Settle (orienting)
// 10–60s:  Reset — physiological sigh cycles (2 in / 1 out, ~5s each cycle)
// 60–90s: Core — heart coherence (5.5 in / 5.5 out)
// 90–120s: Seat — occupy the Horizon
const PHASES = [
  { from: 0,   to: 10,  stage: 'Settle', label: 'Land in the body' },
  { from: 10,  to: 60,  stage: 'Reset',  label: 'Physiological sigh — two short inhales, one long exhale' },
  { from: 60,  to: 90,  stage: 'Core',   label: 'Heart coherence — slow, even breath' },
  { from: 90,  to: 120, stage: 'Settle In',   label: 'Settle into your steady center — already done' },
];

const getPhase = (elapsed) => PHASES.find(p => elapsed >= p.from && elapsed < p.to) || PHASES[PHASES.length - 1];

export default function SovereignAnchor() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);

  const isHidden = HIDDEN_ROUTES.some(r => location.pathname.startsWith(r));

  useEffect(() => {
    if (!open) {
      setElapsed(0);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    setElapsed(0);
    intervalRef.current = setInterval(() => {
      setElapsed(prev => {
        if (prev >= TOTAL_DURATION) {
          clearInterval(intervalRef.current);
          return TOTAL_DURATION;
        }
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [open]);

  if (isHidden) return null;

  const phase = getPhase(elapsed);
  const progress = (elapsed / TOTAL_DURATION) * 100;
  const remaining = Math.max(0, TOTAL_DURATION - elapsed);

  // Breathing scale: organic in/out cycle ~5s based on phase
  const cycleSec = phase.stage === 'Core' ? 11 : phase.stage === 'Reset' ? 5 : 8;  const cyclePos = (elapsed % cycleSec) / cycleSec;
  const scale = 1 + 0.15 * Math.sin(cyclePos * Math.PI * 2);

  return (
    <>
      {/* Floating Compass Button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="sovereign-anchor-button"
        aria-label="Anchor — quick reset"
        className="fixed z-50 group bottom-24 lg:bottom-8 right-5 lg:right-8 w-14 h-14 rounded-full bg-dark/90 backdrop-blur-lg border border-gold/40 flex items-center justify-center shadow-lg hover:scale-105"
        title="Tap to settle in"
        style={{
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          boxShadow: '0 0 24px rgba(198, 178, 143, 0.25), 0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <span className="absolute inset-0 rounded-full bg-gold/15 animate-breathe" />
        <Compass size={22} className="text-gold relative z-10" strokeWidth={1.5} />
      </button>

      {/* Modal Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          data-testid="sovereign-anchor-modal"
          style={{
            background: 'radial-gradient(circle at center, rgba(31,33,32,0.92), rgba(15,16,16,0.98))',
            backdropFilter: 'blur(16px)',
          }}
        >
          {/* Close */}
          <button
            onClick={() => setOpen(false)}
            data-testid="sovereign-anchor-close"
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-bone/10 hover:bg-bone/20 flex items-center justify-center text-bone/70 hover:text-bone"
            style={{ transition: 'background-color 0.2s ease' }}
          >
            <X size={18} />
          </button>

          {/* Stars */}
          {Array.from({ length: 30 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-bone"
              style={{
                left: `${(i * 37) % 100}%`,
                top: `${(i * 53) % 100}%`,
                width: 2,
                height: 2,
                opacity: 0.2 + ((i * 7) % 5) / 10,
                animation: `breathe ${3 + (i % 4)}s ease-in-out ${i * 0.2}s infinite alternate`,
              }}
            />
          ))}

          <div className="relative w-full max-w-md text-center px-6 animate-fade-up">
            {/* Stage tag */}
            <p className="text-gold font-sans text-[11px] tracking-[0.4em] uppercase mb-6">
              Anchor
            </p>
            <p className="text-bone/70 font-sans text-[11px] tracking-[0.35em] uppercase mb-6 -mt-3" data-testid="sovereign-stage">
              Phase · {phase.stage}
            </p>

            {/* Breathing orb */}
            <div className="relative w-64 h-64 mx-auto mb-10">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'radial-gradient(circle at center, rgba(198,178,143,0.45), rgba(198,178,143,0.05) 70%)',
                  transform: `scale(${scale})`,
                  transition: 'transform 1s ease-in-out',
                }}
              />
              <div
                className="absolute inset-8 rounded-full border border-gold/30"
                style={{ transform: `scale(${1 + (1 - scale) * 0.5})`, transition: 'transform 1s ease-in-out' }}
              />
              <div
                className="absolute inset-16 rounded-full border border-gold/50"
                style={{ transform: `scale(${scale * 0.95})`, transition: 'transform 1s ease-in-out' }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <Compass size={36} className="text-gold/80" strokeWidth={1} />
              </div>
            </div>

            <h2 className="font-serif text-2xl sm:text-3xl text-bone leading-snug font-light mb-3">
              {phase.label}
            </h2>
            <p className="text-bone/50 font-sans text-xs tracking-[0.3em] uppercase mb-8" data-testid="sovereign-timer">
              {remaining}s remaining
            </p>

            {/* Progress */}
            <div className="h-[2px] bg-bone/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-gold/40 to-gold rounded-full"
                style={{ width: `${progress}%`, transition: 'width 1s linear' }}
              />
            </div>

            {elapsed >= TOTAL_DURATION && (
              <button
                onClick={() => setOpen(false)}
                data-testid="sovereign-anchor-finish"
                className="mt-10 px-8 h-12 bg-gold text-dark rounded-xl font-sans text-xs tracking-[0.25em] uppercase hover:bg-gold/90"
                style={{ transition: 'background-color 0.2s ease' }}
              >
                Return
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
